import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import type { ContactPerson } from "@/types/contact";

type ContactPersonWithFunctie = ContactPerson & { functie: string };

type ContactPersonCardProps = {
  title: string;
  icon: LucideIcon;
  tooltip?: string;
  fieldTooltips?: Partial<Record<keyof ContactPersonWithFunctie, string>>;
  person: ContactPersonWithFunctie;
  onFieldChange: (field: keyof ContactPersonWithFunctie, value: string) => void;
  headerActions?: ReactNode;
  personKey: string;
  fieldErrors: Record<string, string>;
};

function FieldError({
  errorKey,
  errors,
}: {
  errorKey: string;
  errors: Record<string, string>;
}) {
  const error = errors[errorKey];
  if (!error) return null;

  return (
    <div className="flex items-center gap-2 mt-1">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
      <span className="text-xs text-destructive">{error}</span>
    </div>
  );
}

function ContactPersonCard({
  title,
  icon: Icon,
  tooltip,
  fieldTooltips,
  person,
  onFieldChange,
  headerActions,
  personKey,
  fieldErrors,
}: ContactPersonCardProps) {
  return (
    <div className="flex flex-col h-full border border-ds-border-200 rounded-xl p-4 space-y-3">
      <div className="">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-ds-blue-600" />
          <h4 className="text-sm font-semibold text-navy-900">{title}</h4>
          {tooltip && <FieldTooltip content={tooltip} />}
        </div>
        <div className="lg:min-h-[2.75rem] flex flex-col justify-start text-xs text-ds-text-600">
          {headerActions}
        </div>
      </div>
      <div className="space-y-3 flex-1">
        <div>
          <Label className="text-xs">
            Nume <span className="text-destructive ml-1">*</span>
          </Label>
          <Input
            value={person.nume}
            onChange={(e) => onFieldChange("nume", e.target.value)}
            placeholder="Nume"
            className={cn(
              "text-xs md:text-sm",
              fieldErrors[`${personKey}.nume`] && "border-destructive"
            )}
          />
          <FieldError errorKey={`${personKey}.nume`} errors={fieldErrors} />
        </div>
        <div>
          <Label className="text-xs">
            Prenume <span className="text-destructive ml-1">*</span>
          </Label>
          <Input
            value={person.prenume}
            onChange={(e) => onFieldChange("prenume", e.target.value)}
            placeholder="Prenume"
            className={cn(
              "text-xs md:text-sm",
              fieldErrors[`${personKey}.prenume`] && "border-destructive"
            )}
          />
          <FieldError errorKey={`${personKey}.prenume`} errors={fieldErrors} />
        </div>
        <div>
          <Label className="text-xs">
            Funcție <span className="text-destructive ml-1">*</span>
          </Label>
          <Input
            value={person.functie}
            onChange={(e) => onFieldChange("functie", e.target.value)}
            placeholder="Funcția în cadrul organizației"
            className={cn(
              "text-xs md:text-sm",
              fieldErrors[`${personKey}.functie`] && "border-destructive"
            )}
          />
          <FieldError errorKey={`${personKey}.functie`} errors={fieldErrors} />
        </div>
        <div>
          <Label className="text-xs inline-flex items-center gap-1">
            Email <span className="text-destructive ml-1">*</span>
            {fieldTooltips?.email && (
              <FieldTooltip content={fieldTooltips.email} />
            )}
          </Label>
          <Input
            type="email"
            value={person.email}
            onChange={(e) => onFieldChange("email", e.target.value)}
            placeholder="email@companie.ro"
            className={cn(
              "text-xs md:text-sm",
              fieldErrors[`${personKey}.email`] && "border-destructive"
            )}
          />
          <FieldError errorKey={`${personKey}.email`} errors={fieldErrors} />
        </div>
        <div>
          <Label className="text-xs">Telefon</Label>
          <Input
            value={person.telefon}
            onChange={(e) => onFieldChange("telefon", e.target.value)}
            placeholder="+40..."
            className="text-xs md:text-sm"
          />
        </div>
      </div>
    </div>
  );
}

export default ContactPersonCard;
